import { MenuItem, Slider, Stack, TextField, Typography } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { type CameraVisionSettings } from '../../models/index.js';

const PREPROCESSING_OPTIONS: Array<{ value: CameraVisionSettings['preprocessing']; label: string }> = [
  { value: 'auto', label: 'Auto (IR cleanup at night)' },
  { value: 'none', label: 'None' },
  { value: 'ir_normalize', label: 'IR normalize (burn + contrast)' },
  { value: 'contrast', label: 'Contrast boost (CLAHE)' },
  { value: 'denoise', label: 'Denoise' },
];

interface Props {
  value: CameraVisionSettings;
  onChange: (value: CameraVisionSettings) => void;
}

const VisionSettingsFields: FC<Props> = ({ value, onChange }) => {
  const update = (patch: Partial<CameraVisionSettings>) => onChange({ ...value, ...patch });

  return (
    <Stack spacing={2}>
      <TextField
        select
        size="small"
        label="Stream (for new recordings)"
        value={value.streamQuality}
        onChange={(event) => update({ streamQuality: event.target.value as CameraVisionSettings['streamQuality'] })}
      >
        <MenuItem value="low">LOW — 640×360</MenuItem>
        <MenuItem value="high">HIGH — 1280×720</MenuItem>
      </TextField>

      <TextField
        select
        size="small"
        label="Preprocessing"
        value={value.preprocessing}
        onChange={(event) => update({ preprocessing: event.target.value as CameraVisionSettings['preprocessing'] })}
      >
        {PREPROCESSING_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>

      <Stack direction="row" spacing={2}>
        <TextField
          select
          fullWidth
          size="small"
          label="YOLO working size"
          value={value.imageSize}
          onChange={(event) => update({ imageSize: Number(event.target.value) as CameraVisionSettings['imageSize'] })}
        >
          {[640, 960, 1280].map((size) => <MenuItem key={size} value={size}>{size}px</MenuItem>)}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Model"
          value={value.modelSize}
          onChange={(event) => update({ modelSize: event.target.value as CameraVisionSettings['modelSize'] })}
        >
          <MenuItem value="n">Nano (fastest)</MenuItem>
          <MenuItem value="s">Small</MenuItem>
          <MenuItem value="m">Medium (most accurate)</MenuItem>
        </TextField>
      </Stack>

      <ConfidenceSlider value={value.confidence} onChange={(confidence) => update({ confidence })} />
    </Stack>
  );
};

const ConfidenceSlider: FC<{ value: number; onChange: (value: number) => void }> = ({ value, onChange }) => {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <Stack>
      <Typography variant="caption" color="text.secondary">Confidence ≥ {draft.toFixed(2)}</Typography>
      <Slider
        size="small"
        value={draft}
        min={0.05}
        max={0.95}
        step={0.05}
        onChange={(_event, next) => setDraft(next as number)}
        onChangeCommitted={(_event, next) => onChange(next as number)}
      />
    </Stack>
  );
};

export default VisionSettingsFields;
